import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiExternalLink, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { dashboardService } from '../api/services/dashboardService';
import { ChatSessionDetail, PaginatedResponse, TableCountsResponse } from '../api/types';
import { getUserFriendlyErrorMessage } from '../api/utils/errorHandler';

interface ChatThread {
  id: string;
  playerId: string;
  startedAt: string;
  timeAgo: string;
  firstMessage?: string;
  messageCount?: number;
  totalChats?: number;
}

interface PlayerThread {
  thread_id: number;
  player_id: string;
  started_at: string;
  total: number | null;
}

const ChatThreads: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
  });
  const [tableCounts, setTableCounts] = useState<TableCountsResponse | null>(null);
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const [playerThreads, setPlayerThreads] = useState<Record<string, PlayerThread[]>>({});
  const [loadingThreads, setLoadingThreads] = useState<Set<string>>(new Set());
  const [threadDetails, setThreadDetails] = useState<Record<number, { firstMessage: string; messageCount: number }>>({});
  const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set());

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  // Format date to relative time
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds} saniye önce`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} dakika önce`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} saat önce`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} gün önce`;
    }
  };

  // Helper function to check if content is JSON
  const isJsonString = (str: string): boolean => {
    try {
      const trimmed = str.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
          (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        JSON.parse(trimmed);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  // Fetch first message and message count for a thread
  const fetchThreadDetails = async (threadId: string): Promise<{ firstMessage: string; messageCount: number }> => {
    try {
      const messages = await dashboardService.getChatMessages(threadId);
      
      // Filter out empty and JSON messages
      const validMessages = messages.filter((msg) => {
        if (!msg.content || msg.content.trim() === '') {
          return false;
        }
        if (isJsonString(msg.content)) {
          return false;
        }
        return true;
      });

      // Find first human message
      const firstHumanMessage = validMessages.find((msg) => msg.type === 'human');
      const firstMessage = firstHumanMessage?.content || validMessages[0]?.content || '';

      // Count only human messages
      const humanMessageCount = validMessages.filter((msg) => msg.type === 'human').length;

      return {
        firstMessage: firstMessage.length > 100 ? firstMessage.substring(0, 100) + '...' : firstMessage,
        messageCount: humanMessageCount,
      };
    } catch (error) {
      console.error(`Error fetching details for thread ${threadId}:`, error);
      return { firstMessage: '', messageCount: 0 };
    }
  };

  // Fetch chat threads from API
  const fetchChatThreads = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response: PaginatedResponse<ChatSessionDetail> = await dashboardService.getChatSessionDetails({
        page,
      });

      // Transform API data to local format (without details first)
      // Filter out threads where total === 1
      // Group by player_id and keep only the most recent thread for each player
      const playerThreadMap = new Map<string, ChatThread>();
      
      response.data
        .filter((chatDetail) => chatDetail.total !== 1)
        .forEach((chatDetail) => {
          const thread: ChatThread = {
            id: chatDetail.thread_id.toString(),
            playerId: chatDetail.player_id,
            startedAt: chatDetail.started_at,
            timeAgo: formatDate(chatDetail.started_at),
            totalChats: chatDetail.total,
          };
          
          // Keep only the most recent thread for each player
          const existingThread = playerThreadMap.get(chatDetail.player_id);
          if (!existingThread || new Date(chatDetail.started_at) > new Date(existingThread.startedAt)) {
            playerThreadMap.set(chatDetail.player_id, thread);
          }
        });
      
      const transformedThreads: ChatThread[] = Array.from(playerThreadMap.values());

      setThreads(transformedThreads);
      setPagination({
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      });

      // Fetch details for each thread sequentially (one at a time) to avoid overwhelming the API
      const threadsWithDetails: ChatThread[] = [];
      for (let i = 0; i < transformedThreads.length; i++) {
        const thread = transformedThreads[i];
        const details = await fetchThreadDetails(thread.id);
        threadsWithDetails.push({ ...thread, ...details });
        
        // Update UI progressively as each thread detail is loaded
        const updatedThreads = [
          ...threadsWithDetails,
          ...transformedThreads.slice(i + 1)
        ];
        setThreads(updatedThreads);
        
        // Small delay between requests to avoid overwhelming the API
        if (i < transformedThreads.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
    } catch (err: any) {
      const errorMessage = getUserFriendlyErrorMessage(err);
      setError(errorMessage);
      console.error('Error fetching chat threads:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch table counts for stats
  const fetchTableCounts = async () => {
    try {
      const response = await dashboardService.getTableCounts();
      setTableCounts(response);
    } catch (err: any) {
      console.error('Error fetching table counts:', err);
    }
  };

  useEffect(() => {
    fetchChatThreads(currentPage);
    fetchTableCounts();
  }, [currentPage]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchChatThreads(currentPage);
    fetchTableCounts();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Calculate percentage change
  const calculateChange = (current: number, previous: number): string => {
    if (previous === 0) return '+0%';
    const change = ((current - previous) / previous) * 100;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get daily change from metrics (daily growth percentage)
  const getDailyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_daily`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get weekly change from metrics (weekly growth percentage)
  const getWeeklyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_weekly`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get monthly change from metrics (monthly growth percentage)
  const getMonthlyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_monthly`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get daily change from Daily metrics (daily growth percentage)
  const getDailyValue = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_daily`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get today's count by metric type
  const getTodayCount = (metricType: string): number => {
    if (!tableCounts) return 0;
    const count = tableCounts.total_counts.find(tc => tc.metric_type === metricType);
    return count?.total_today || 0;
  };

  // Fetch player threads when accordion is expanded
  const fetchPlayerThreads = async (playerId: string) => {
    if (playerThreads[playerId] || loadingThreads.has(playerId)) return;
    
    try {
      setLoadingThreads(prev => new Set(prev).add(playerId));
      const threads = await dashboardService.getChatSessionsByPlayerId(playerId);
      setPlayerThreads(prev => ({
        ...prev,
        [playerId]: threads,
      }));
      
      // Fetch details for each thread
      threads.forEach(async (thread) => {
        if (!threadDetails[thread.thread_id] && !loadingDetails.has(thread.thread_id)) {
          setLoadingDetails(prev => new Set(prev).add(thread.thread_id));
          try {
            const details = await fetchThreadDetails(thread.thread_id.toString());
            setThreadDetails(prev => ({
              ...prev,
              [thread.thread_id]: details,
            }));
          } catch (err) {
            console.error(`Error fetching details for thread ${thread.thread_id}:`, err);
          } finally {
            setLoadingDetails(prev => {
              const newSet = new Set(prev);
              newSet.delete(thread.thread_id);
              return newSet;
            });
          }
        }
      });
    } catch (err: any) {
      console.error(`Error fetching threads for player ${playerId}:`, err);
    } finally {
      setLoadingThreads(prev => {
        const newSet = new Set(prev);
        newSet.delete(playerId);
        return newSet;
      });
    }
  };

  // Toggle accordion
  const toggleAccordion = (threadId: string, playerId: string) => {
    setExpandedThreads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(threadId)) {
        newSet.delete(threadId);
      } else {
        newSet.add(threadId);
        fetchPlayerThreads(playerId);
      }
      return newSet;
    });
  };

  const stats = [
    { 
      label: 'Toplam Konuşma', 
      value: pagination.total.toLocaleString(), 
      change: getDailyChange('chat_session'), 
      weeklyChange: getWeeklyChange('chat_session'),
      monthlyChange: getMonthlyChange('chat_session'),
      color: 'blue' 
    },
    { 
      label: 'Konuşma', 
      value: getTodayCount('chat_session').toLocaleString('tr-TR'), 
      color: 'green' 
    },
  ];

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
        <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent mb-2">
          Sohbet Oturumları
        </h1>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            className="flex items-center gap-2 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl hover:scale-105 transform disabled:hover:scale-100"
          >
            {renderIcon(FiRefreshCw, `w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`)}
            <span>Yenile</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="glass-strong bg-gradient-to-r from-red-50/80 to-rose-50/80 dark:from-red-900/30 dark:to-rose-900/30 border border-red-200/50 dark:border-red-800/50 rounded-2xl p-4 mb-6 shadow-lg animate-slide-up">
          <div className="flex items-center gap-3 text-red-700 dark:text-red-300">
            <span className="text-2xl">⚠️</span>
            <p className="font-semibold">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="relative glass-strong rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 p-6 border hover:border-indigo-300 dark:hover:border-indigo-600 overflow-hidden group transform hover:-translate-y-1 animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
            {/* Animated gradient background */}
            <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-30 group-hover:opacity-50 group-hover:scale-150 transition-all duration-700 ${
              stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
              stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
              'bg-indigo-100 dark:bg-indigo-900/30'
            }`}></div>
            <div className={`absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-all duration-700 ${
              stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
              stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
              'bg-indigo-100 dark:bg-indigo-900/30'
            }`}></div>
            
            {/* Shimmer effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <div className="absolute inset-0 shimmer"></div>
            </div>
            
            <div className="relative z-10">
              <p className="text-sm font-bold text-slate-600 dark:text-slate-300 mb-3 uppercase tracking-wider">{stat.label}</p>
              <p className={`text-5xl font-extrabold mb-4 bg-gradient-to-r ${
                stat.color === 'blue' ? 'from-blue-600 via-blue-500 to-blue-400' :
                stat.color === 'green' ? 'from-green-600 via-green-500 to-green-400' :
                'from-indigo-600 via-indigo-500 to-indigo-400'
              } bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300`}>
                {stat.value}
              </p>
              {stat.label === 'Toplam Konuşma' && (
                <div className="flex flex-col gap-3 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                  {stat.change && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Günlük</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.change.startsWith('+') || stat.change.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.change}
                      </span>
                    </div>
                  )}
                  {stat.weeklyChange && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Haftalık</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.weeklyChange.startsWith('+') || stat.weeklyChange.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.weeklyChange}
                      </span>
                    </div>
                  )}
                  {stat.monthlyChange && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Aylık</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.monthlyChange.startsWith('+') || stat.monthlyChange.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.monthlyChange}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="glass-strong rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border transform hover:scale-[1.01] animate-fade-in">
        <div className="px-6 py-5 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-indigo-50/50 via-purple-50/50 to-pink-50/50 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20 backdrop-blur-sm">
          <h2 className="text-xl font-bold gradient-text">Son Sohbet Konuşmaları</h2>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loading && threads.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            </div>
          ) : threads.length === 0 ? (
            <div className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
              Sohbet oturumu bulunamadı
            </div>
          ) : (
            threads.map((thread) => {
              const isExpanded = expandedThreads.has(thread.id);
              const playerThreadsList = playerThreads[thread.playerId] || [];
              const isLoading = loadingThreads.has(thread.playerId);
              
              return (
                <div key={thread.id} className="border-b border-gray-200/50 dark:border-gray-700/50 last:border-b-0">
                  {/* Main Thread */}
                  <div className="px-6 py-4 hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-purple-50/50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-300">
              <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <button
                            onClick={() => toggleAccordion(thread.id, thread.playerId)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                          >
                            {renderIcon(isExpanded ? FiChevronDown : FiChevronRight, "w-4 h-4 text-slate-600 dark:text-slate-400")}
                          </button>
                          <h3 
                            className="text-sm font-semibold text-slate-900 dark:text-white cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400"
                    onClick={() => navigate(`/chat-thread/${thread.id}`)}
                  >
                        {thread.playerId}
                      </h3>
                      {thread.messageCount !== undefined && thread.messageCount > 0 && (
                        <span className="px-2 py-0.5 bg-indigo-500 dark:bg-indigo-600 text-white text-xs font-semibold rounded-full">
                          {thread.messageCount} mesaj
                      </span>
                    )}
                          {thread.totalChats !== undefined && thread.totalChats !== 1 && (
                            <span className="px-2 py-0.5 bg-purple-500 dark:bg-purple-600 text-white text-xs font-semibold rounded-full">
                              {thread.totalChats} toplam chat
                      </span>
                    )}
                    </div>
                    {thread.firstMessage && (
                          <p className="text-sm text-slate-600 dark:text-slate-300 mb-1 line-clamp-2 ml-7">
                        {thread.firstMessage}
                      </p>
                    )}
                        <p className="text-xs text-slate-400 dark:text-slate-500 ml-7">{thread.timeAgo}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const baseUrl = window.location.origin;
                      const newUrl = `${baseUrl}#/chat-thread/${thread.id}`;
                      window.open(newUrl, '_blank');
                    }}
                    className="ml-4 p-2 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                    title="Yeni sekmede aç"
                  >
                    {renderIcon(FiExternalLink, "w-5 h-5")}
                  </button>
                </div>
              </div>
                  
                  {/* Accordion Content */}
                  {isExpanded && (
                    <div className="px-6 py-3 glass bg-gradient-to-r from-gray-50/50 to-indigo-50/30 dark:from-gray-900/50 dark:to-indigo-900/20 border-t border-gray-200/50 dark:border-gray-700/50">
                      {isLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                        </div>
                      ) : playerThreadsList.length === 0 ? (
                        <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-2">
                          Başka thread bulunamadı
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {playerThreadsList
                            .filter((playerThread) => playerThread.thread_id.toString() !== thread.id)
                            .map((playerThread) => {
                            const details = threadDetails[playerThread.thread_id];
                            const isLoadingDetail = loadingDetails.has(playerThread.thread_id);
                            
                            return (
                              <div
                                key={playerThread.thread_id}
                                className="px-4 py-3 glass rounded-xl border border-gray-200/50 dark:border-gray-700/50 hover:border-indigo-300 dark:hover:border-indigo-600 hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-purple-50/50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-300 transform hover:scale-[1.02]"
                              >
                                <div 
                                  className="cursor-pointer"
                                  onClick={() => navigate(`/chat-thread/${playerThread.thread_id}`)}
                                >
                                  <div className="flex items-center space-x-3 mb-2">
                                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400">
                                      {playerThread.player_id}
                                    </h3>
                                    {details && details.messageCount > 0 && (
                                      <span className="px-2 py-0.5 bg-indigo-500 dark:bg-indigo-600 text-white text-xs font-semibold rounded-full">
                                        {details.messageCount} mesaj
                                      </span>
                                    )}
                                    {isLoadingDetail && (
                                      <span className="text-xs text-slate-500 dark:text-slate-400">
                                        Yükleniyor...
                                      </span>
                                    )}
                                    {playerThread.total !== null && playerThread.total !== 1 && (
                                      <span className="px-2 py-0.5 bg-purple-500 dark:bg-purple-600 text-white text-xs font-semibold rounded-full">
                                        {playerThread.total} toplam chat
                                      </span>
                                    )}
                                  </div>
                                  {details && details.firstMessage && (
                                    <p className="text-sm text-slate-600 dark:text-slate-300 mb-1 line-clamp-2">
                                      {details.firstMessage}
                                    </p>
                                  )}
                                  <p className="text-xs text-slate-400 dark:text-slate-500">{formatDate(playerThread.started_at)}</p>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const baseUrl = window.location.origin;
                                    const newUrl = `${baseUrl}#/chat-thread/${playerThread.thread_id}`;
                                    window.open(newUrl, '_blank');
                                  }}
                                  className="mt-2 p-1.5 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                                  title="Yeni sekmede aç"
                                >
                                  {renderIcon(FiExternalLink, "w-4 h-4")}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Toplam {pagination.total} kayıttan sayfa {currentPage} / {pagination.totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 glass rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 border border-gray-300/50 dark:border-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 transform disabled:hover:scale-100 shadow-sm hover:shadow-md"
              >
                Önceki
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                  <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                    className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300 ${
                        currentPage === pageNum
                        ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-500 dark:via-purple-500 dark:to-pink-500 text-white shadow-lg scale-110'
                        : 'text-slate-700 dark:text-slate-300 glass border border-gray-300/50 dark:border-gray-600/50 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 hover:scale-105 transform shadow-sm hover:shadow-md'
                    }`}
                  >
                      {pageNum}
                  </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages || loading}
                className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 glass rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 border border-gray-300/50 dark:border-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 transform disabled:hover:scale-100 shadow-sm hover:shadow-md"
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatThreads;

